import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import ManagePasswordModal from './ManagePasswordModal';
import {
  BarChart3,
  ChevronDown,
  CreditCard,
  Settings,
  FileText,
  Building2,
  LogOut,
  Menu,
  Home,
  CalendarClock,
  PanelLeft,
  ShieldCheck,
  Activity,
  Mic,
  Music,
  PhoneCall,
  UserPlus,
  KeyRound,
  Server,
  ArrowLeftRight,
  Wand2,
  Stethoscope,
  Bot,
  BookOpen,
  Award,
  Clock as ClockIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { canAccessAnalytics, isAdminUser, isTimelinerUser, isClinicalSimUser } from '../lib/userAccess';

// ─── Nav Item ─────────────────────────────────────────────────────────────────
function NavItem({ icon, label, to, collapsed, isActive, disabled = false, currentPath = '/home' }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <NavLink
      to={disabled ? currentPath : to}
      title={collapsed ? label : ''}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : '8px',
        padding: collapsed ? '8px 0' : '7px 12px 7px 12px',
        pointerEvents: disabled ? 'none' : 'auto',
        borderLeft: isActive 
          ? '3px solid #f0530f' 
          : isHovered && !disabled ? '3px solid #fde8d8' : '3px solid transparent',
        borderRadius: 0,
        margin: '1px 0px',
        color: disabled ? '#cbd5e1' : isActive ? '#c43b00' : isHovered ? '#0f172a' : '#64748b',
        background: isHovered && !disabled ? 'rgba(240, 83, 15, 0.04)' : 'transparent',
        textDecoration: 'none',
        transition: 'all 0.12s ease',
        fontSize: '13px',
        fontWeight: isActive ? 500 : 400,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.75 : 1,
      }}
    >
      {icon}
      <span style={{
        opacity: collapsed ? 0 : 1,
        maxWidth: collapsed ? 0 : '180px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        transition: 'opacity 0.12s ease, max-width 0.18s ease',
      }}>
        {label}
      </span>
    </NavLink>
  );
}


// ─── Sidebar Shell ─────────────────────────────────────────────────────────────
export const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const isAdmin = isAdminUser(user);
  const isTimeliner = isTimelinerUser(user) || isAdmin;
  const isClinical = isClinicalSimUser(user);

  // Step 1 — collapsed state with localStorage persistence
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  // Sync a data-attribute on <html> so App.css can override ml-60 without touching pages
  useEffect(() => {
    document.documentElement.setAttribute('data-sidebar', collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  // Also initialize on mount (for first load from localStorage)
  useEffect(() => {
    const initial = localStorage.getItem('sidebar_collapsed') === 'true';
    document.documentElement.setAttribute('data-sidebar', initial ? 'collapsed' : 'expanded');
  }, []);

  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  const navItems = [
    ...(!isClinical ? [
      { icon: <Home size={18} />, label: 'Home', path: '/home' },
      { icon: <BarChart3 size={18} />, label: 'Analytics', path: '/analytics', disabled: !canAccessAnalytics(user) },
      { icon: <Building2 size={18} />, label: 'Organisation', path: '/organisation' },
      ...(isTimeliner ? [{ icon: <Activity size={18} />, label: 'Timeliner', path: '/timeliner' }] : []),
    ] : []),
    ...(isClinical ? [
      { icon: <BookOpen size={18} />, label: 'Training Library', path: '/cs/library' },
      { icon: <ClockIcon size={18} />, label: 'My Sessions', path: '/cs/sessions' },
      { icon: <FileText size={18} />, label: 'Prescriptions', path: '/cs/prescriptions' },
      { icon: <Award size={18} />, label: 'Scorecard', path: '/cs/scorecard' },
    ] : []),
    ...(isAdmin ? [
      { icon: <ShieldCheck size={18} />, label: 'Admin', path: '/admin-dashboard' },
      { icon: <Mic size={18} />, label: 'Voice', path: '/voice-dashboard' },
      { icon: <Music size={18} />, label: 'Background Audio', path: '/background-voice-dashboard' },
      { icon: <UserPlus size={18} />, label: 'Create Account', path: '/create-account' },
      { icon: <PhoneCall size={18} />, label: 'DID Numbers', path: '/did-numbers-dashboard' },
      { icon: <Building2 size={18} />, label: 'Providers', path: '/providers-dashboard' },
      { icon: <Server size={18} />, label: 'Deploy GPU', path: '/deploy-gpu-dashboard' },
      { icon: <ArrowLeftRight size={18} />, label: 'Move Prompt', path: '/move-prompt' },
      { icon: <Wand2 size={18} />, label: 'Instant Clone', path: '/instant-clone' },
      { icon: <Stethoscope size={18} />, label: 'Patient Library', path: '/patient-library' },
      { icon: <Bot size={18} />, label: 'Demo Prompts', path: '/demo-agent-prompts' },
      { icon: <Activity size={18} />, label: 'Current Stats', path: '/admin/current-stats' },
    ] : []),
  ];

  // ─── Desktop Sidebar Content ─────────────────────────────────────────────────
  const DesktopSidebar = () => (
    <div
      className="sidebar-shell"
      data-testid="sidebar"
      style={{
        width: collapsed ? '52px' : '220px',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        flexShrink: 0,
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid #f1f5f9',
        background: '#ffffff',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      {/* Step 3 — Toggle Button */}
      <button
        onClick={toggle}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        data-testid="sidebar-toggle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '11px 0' : '11px 14px',
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#94a3b8',
          flexShrink: 0,
          transition: 'color 0.12s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#0f172a'}
        onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
      >
        <PanelLeft size={18} />
      </button>

      {/* Step 4 — Logo / Brand */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: collapsed ? '0 0 10px 0' : '0 14px 10px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderBottom: '1px solid #f8fafc',
      }}>
        <NavLink to="/home" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? '0' : '10px', 
          textDecoration: 'none',
          width: '100%' 
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '7px',
            width: '28px',
            height: '28px',
            background: 'linear-gradient(135deg, #f0530f, #ff8a4c)',
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 700, color: '#fff', fontSize: '12px' }}>T</span>
          </div>
          <span style={{
            opacity: collapsed ? 0 : 1,
            maxWidth: collapsed ? 0 : '150px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            transition: 'opacity 0.12s ease, max-width 0.18s ease',
            fontWeight: 600,
            fontSize: '13px',
            color: '#0f172a',
            letterSpacing: '-0.01em',
          }}>
            TechLadder.ai
          </span>
        </NavLink>
      </div>

      {/* Workspace switcher — hidden when collapsed */}
      <div style={{
        padding: collapsed ? '8px 0' : '8px 10px',
        borderBottom: collapsed ? 'none' : '1px solid #f1f5f9',
        overflow: 'hidden',
      }}>
        <div style={{
          opacity: collapsed ? 0 : 1,
          maxHeight: collapsed ? 0 : '40px',
          overflow: 'hidden',
          transition: 'opacity 0.15s ease, max-height 0.2s ease',
        }}>
          <Button
            variant="ghost"
            className="justify-between w-full px-3 text-slate-600 hover:bg-slate-50 h-9"
            data-testid="workspace-switcher"
          >
            <span className="text-sm">Default Workspace</span>
            <ChevronDown className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Step 5 — Navigation Items */}
      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto', overflowX: 'hidden' }}>
        {navItems.map(item => (
          <NavItem
            key={item.path}
            icon={item.icon}
            label={item.label}
            to={item.path}
            collapsed={collapsed}
            disabled={item.disabled}
            currentPath={location.pathname}
            isActive={
              item.path === '/home'
                ? location.pathname === '/home' || location.pathname.startsWith('/home/')
                : location.pathname === item.path
            }
          />
        ))}
      </nav>

      {/* Step 6 — User Profile at Bottom */}
      <div style={{ borderTop: '1px solid #f8fafc', flexShrink: 0 }}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-profile-dropdown"
              title={collapsed ? (user?.name || 'User') : ''}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: '10px',
                padding: collapsed ? '12px 0' : '12px 10px',
                width: '100%',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar style={{ width: 32, height: 32, flexShrink: 0 }}>
                <AvatarFallback style={{ background: '#7c3aed', color: '#fff', fontSize: '13px' }}>
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>

              {/* Name + Email + Chevron — hidden when collapsed */}
              <div style={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
                opacity: collapsed ? 0 : 1,
                maxWidth: collapsed ? 0 : '160px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'opacity 0.15s ease, max-width 0.2s ease',
              }}>
                <div style={{ textAlign: 'left', minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user?.name || 'Arpan Shukla'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user?.email || 'arpan.gsc.biet@gmail.com'}
                  </div>
                </div>
                <ChevronDown style={{ width: 14, height: 14, color: '#94a3b8', flexShrink: 0 }} />
              </div>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56" data-testid="user-dropdown-menu">
            <DropdownMenuItem data-testid="dropdown-billing">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing Plan
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="dropdown-concurrency">
              <Settings className="w-4 h-4 mr-2" />
              Manage Concurrency
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="dropdown-logs">
              <FileText className="w-4 h-4 mr-2" />
              Workspace Logs
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="dropdown-company">
              <Building2 className="w-4 h-4 mr-2" />
              Company Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/developer-api-keys')} data-testid="dropdown-developer-api">
              <KeyRound className="w-4 h-4 mr-2" />
              Manage Developer API
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setPasswordModalOpen(true)} data-testid="dropdown-manage-password">
              <KeyRound className="w-4 h-4 mr-2" />
              Manage Password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600" data-testid="dropdown-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar — hidden below lg breakpoint */}
      <div className="hidden lg:block">
        <DesktopSidebar />
      </div>

      {/* Mobile Sidebar — Sheet drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="fixed z-40 bg-white lg:hidden top-4 left-4 shadow-soft"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="p-0 w-60" data-testid="mobile-sidebar">
          {/* Mobile always uses the full expanded view */}
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Logo */}
            <div style={{ padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
              <NavLink to="/home" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '8px', width: '32px', height: '32px',
                  background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
                }}>
                  <span style={{ fontWeight: 700, color: '#fff', fontSize: '14px' }}>T</span>
                </div>
                <span style={{ fontWeight: 600, fontSize: '15px', color: '#0f172a' }}>TechLadder.ai</span>
              </NavLink>
              <Button variant="ghost" className="justify-between w-full px-3 text-slate-600 hover:bg-slate-50 h-9 mt-3" data-testid="workspace-switcher-mobile">
                <span className="text-sm">Default Workspace</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>

            {/* Nav */}
            <nav style={{ flex: 1, padding: '8px 0' }}>
              {navItems.map(item => (
                <NavLink
                  key={item.path}
                  to={item.disabled ? location.pathname : item.path}
                  onClick={() => setMobileOpen(false)}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 16px', margin: '1px 6px', borderRadius: '8px',
                    textDecoration: 'none', fontSize: '14px',
                    color: item.disabled ? '#cbd5e1' : isActive ? '#0f172a' : '#64748b',
                    background: item.disabled ? 'transparent' : isActive ? '#f1f5f9' : 'transparent',
                    fontWeight: isActive ? 500 : 400,
                    pointerEvents: item.disabled ? 'none' : 'auto',
                    cursor: item.disabled ? 'not-allowed' : 'pointer',
                  })}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* User profile */}
            <div style={{ padding: '12px 10px', borderTop: '1px solid #f1f5f9' }}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: '8px', padding: '6px',
                  }}>
                    <Avatar style={{ width: 32, height: 32 }}>
                      <AvatarFallback style={{ background: '#7c3aed', color: '#fff' }}>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                    </Avatar>
                    <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#0f172a' }}>{user?.name || 'Arpan Shukla'}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{user?.email || 'arpan.gsc.biet@gmail.com'}</div>
                    </div>
                    <ChevronDown style={{ width: 14, height: 14, color: '#94a3b8' }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem><CreditCard className="w-4 h-4 mr-2" />Billing Plan</DropdownMenuItem>
                  <DropdownMenuItem><Settings className="w-4 h-4 mr-2" />Manage Concurrency</DropdownMenuItem>
                  <DropdownMenuItem><FileText className="w-4 h-4 mr-2" />Workspace Logs</DropdownMenuItem>
                  <DropdownMenuItem><Building2 className="w-4 h-4 mr-2" />Company Details</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/developer-api-keys')}><KeyRound className="w-4 h-4 mr-2" />Manage Developer API</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setPasswordModalOpen(true)}><KeyRound className="w-4 h-4 mr-2" />Manage Password</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600"><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Manage Password Modal */}
      <ManagePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </>
  );
};

export default Sidebar;
